import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Flex,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  SimpleGrid,
  Stack,
  Tag,
  Text,
  useColorModeValue,
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
} from "@chakra-ui/react";
import { SearchIcon, ExternalLinkIcon } from "@chakra-ui/icons";
import { useSelector, useDispatch } from "react-redux";

import { publicationDetails } from "./PublicationDetails";
import { fetchData } from "actions/actions";

/* -----------------------------
   Helpers (safe + minimal)
------------------------------ */
const normalize = (s) =>
  (s ?? "")
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");

const extractYear = (s) => {
  // grabs first 4-digit year it finds, e.g., "30 JAN 2026" -> 2026
  const m = (s ?? "").toString().match(/\b(19|20)\d{2}\b/);
  return m ? Number(m[0]) : null;
};

const typeLabel = (t) => {
  const x = (t ?? "").toLowerCase();
  if (x === "journal") return "Journals";
  if (x === "conference") return "Conferences";
  if (x === "patent") return "Patents";
  return "Publications";
};

function PageHeader() {
  const heroBg = useColorModeValue(
    "linear-gradient(135deg, rgba(49,83,105,0.10), rgba(220,36,44,0.10))",
    "linear-gradient(135deg, rgba(49,83,105,0.16), rgba(220,36,44,0.14))"
  );
  const border = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.10)");
  const titleColor = useColorModeValue("#315369", "white");
  const subColor = useColorModeValue("gray.700", "gray.300");
  const accent = "#DC242C";

  return (
    <Box
      pt={{ base: "140px", md: "140px" }}
      pb={{ base: 10, md: 12 }}
      bg={heroBg}
      borderBottom="1px solid"
      borderColor={border}
    >
      <Container maxW="1200px">
        <Stack spacing={3}>
          <HStack spacing={2} flexWrap="wrap">
            <Tag
              size="lg"
              borderRadius="full"
              bg={useColorModeValue("white", "rgba(255,255,255,0.06)")}
              border="1px solid"
              borderColor={border}
              fontWeight="800"
            >
              University of Ottawa
            </Tag>
            <Tag
              size="lg"
              borderRadius="full"
              bg={useColorModeValue("rgba(220,36,44,0.10)", "rgba(220,36,44,0.20)")}
              color={accent}
              fontWeight="900"
            >
              SITE-CAV
            </Tag>
          </HStack>

          <Heading color={titleColor} fontSize={{ base: "3xl", md: "4xl" }} letterSpacing="tight">
            Publications
          </Heading>

          <Text color={subColor} fontSize={{ base: "md", md: "lg" }} maxW="95ch">
            Peer-reviewed outputs from the SITE-CAV team, including journals, conferences, and patents.
          </Text>
        </Stack>
      </Container>
    </Box>
  );
}

function PublicationAccordionList({
  items,
  isMobile,
  emptyText,
  initialOpen = true,
}) {
  const border = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.10)");
  const panelBg = useColorModeValue("white", "rgba(255,255,255,0.03)");
  const titleColor = useColorModeValue("#315369", "white");
  const bodyColor = useColorModeValue("gray.700", "gray.300");
  const accent = "#DC242C";

  if (!items?.length) {
    return (
      <Box
        border="1px solid"
        borderColor={border}
        bg={panelBg}
        borderRadius="22px"
        p={{ base: 6, md: 8 }}
        textAlign="center"
      >
        <Text fontWeight="900" color={titleColor}>
          {emptyText}
        </Text>
      </Box>
    );
  }

  return (
    <Accordion allowMultiple defaultIndex={initialOpen ? [0] : []} display="grid" gap={4}>
      {items.map((p, idx) => (
        <AccordionItem
          key={`${p.title}-${idx}`}
          border="1px solid"
          borderColor={border}
          borderRadius="22px"
          overflow="hidden"
          bg={panelBg}
        >
          <h2>
            <AccordionButton
              px={{ base: 5, md: 6 }}
              py={{ base: 4, md: 5 }}
              _hover={{
                bg: useColorModeValue("rgba(49,83,105,0.06)", "rgba(255,255,255,0.05)"),
              }}
            >
              <Box flex="1" textAlign="left">
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  fontWeight="900"
                  color={titleColor}
                  lineHeight={1.2}
                >
                  {p.title}
                </Text>

                <HStack mt={2} spacing={2} flexWrap="wrap">
                  {p.publicationDate ? (
                    <Tag
                      size="sm"
                      borderRadius="full"
                      bg={useColorModeValue("rgba(0,0,0,0.05)", "rgba(255,255,255,0.08)")}
                      fontWeight="900"
                      color={bodyColor}
                    >
                      {p.publicationDate}
                    </Tag>
                  ) : null}

                  
                </HStack>
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h2>

          <AccordionPanel px={{ base: 5, md: 6 }} pb={{ base: 5, md: 6 }}>
            <Text
              color={bodyColor}
              fontSize={{ base: "md", md: "lg" }}
              textAlign="left"
            >
              {p.writers}
              {p.writers ? " " : ""}
              “{p.title}” {p.conference} {p.publicationDate}.
              {p.link ? (
                <>
                  {" "}
                  Preprint available:{" "}
                  <Button
                    as="a"
                    href={p.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="sm"
                    ml={2}
                    variant="outline"
                    borderRadius="14px"
                    fontWeight="900"
                    leftIcon={<ExternalLinkIcon />}
                  >
                    Open
                  </Button>
                </>
              ) : null}
            </Text>
          </AccordionPanel>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

export default function PublicationsList() {
  const isMobile = useSelector((state) => state.windowSize.isMobile);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchData());
  }, [dispatch]);

  const all = Array.isArray(publicationDetails) ? publicationDetails : [];

  // Controls
  const [query, setQuery] = useState("");
  const [year, setYear] = useState("All");
  const [sortMode, setSortMode] = useState("newest"); // newest | oldest | title
  const [visible, setVisible] = useState({ journal: 5, conference: 5, patent: 5 });

  // Collect available years from your publicationDate strings
  const years = useMemo(() => {
    const ys = new Set();
    for (const p of all) {
      const y = extractYear(p?.publicationDate);
      if (y) ys.add(y);
    }
    return Array.from(ys).sort((a, b) => b - a);
  }, [all]);

  const filteredByType = useMemo(() => {
    const q = normalize(query);
    const yFilter = year === "All" ? null : Number(year);

    const pass = (p) => {
      if (!p) return false;

      const y = extractYear(p.publicationDate);
      if (yFilter && y !== yFilter) return false;

      if (!q) return true;

      const hay = normalize(
        [p.title, p.writers, p.conference, p.publicationDate, p.link, p.type]
          .filter(Boolean)
          .join(" ")
      );

      return hay.includes(q);
    };

    const sortFn = (a, b) => {
      if (sortMode === "title") return (a.title || "").localeCompare(b.title || "");
      const ya = extractYear(a.publicationDate) ?? 0;
      const yb = extractYear(b.publicationDate) ?? 0;
      // newest first
      return sortMode === "oldest" ? ya - yb : yb - ya;
    };

    const journals = all.filter((p) => p.type === "journal" && pass(p)).sort(sortFn);
    const conferences = all.filter((p) => p.type === "conference" && pass(p)).sort(sortFn);
    const patents = all.filter((p) => p.type === "patent" && pass(p)).sort(sortFn);

    return { journals, conferences, patents };
  }, [all, query, year, sortMode]);

  const hasActiveFilter = Boolean(query.trim()) || year !== "All";

  const totalFilteredCount =
    filteredByType.journals.length +
    filteredByType.conferences.length +
    filteredByType.patents.length;

  const visibleSections = [
    {
      label: "Journals",
      items: filteredByType.journals,
      typeKey: "journal",
      emptyText: "Journals will be announced soon, stay tuned!",
    },
    {
      label: "Conferences",
      items: filteredByType.conferences,
      typeKey: "conference",
      emptyText: "Conferences will be announced soon, stay tuned!",
    },
    {
      label: "Patents",
      items: filteredByType.patents,
      typeKey: "patent",
      emptyText: "Patents will be announced soon, stay tuned!",
    },
  ].filter((section) => {
    // If search/filter is active, only keep sections that have results
    if (hasActiveFilter) return section.items.length > 0;

    // Otherwise show all sections normally
    return true;
  });


  // Reset "visible" when filters change
  useEffect(() => {
    setVisible({ journal: 5, conference: 5, patent: 5 });
  }, [query, year, sortMode]);

  const border = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.10)");
  const panelBg = useColorModeValue("white", "rgba(255,255,255,0.03)");

  const Section = ({ label, items, typeKey, emptyText }) => {
    const shown = items.slice(0, visible[typeKey]);

    return (
      <Box>
        <Flex align="center" justify="space-between" mb={4} flexWrap="wrap" gap={3}>
          <Heading fontSize={{ base: "xl", md: "2xl" }} color={useColorModeValue("#315369", "white")}>
            {label}
          </Heading>

          <Tag borderRadius="full" fontWeight="900">
            {items.length} total
          </Tag>
        </Flex>

        <PublicationAccordionList
          items={shown}
          isMobile={isMobile}
          emptyText={emptyText}
          initialOpen={true}
        />

        {items.length > shown.length ? (
          <Flex justify="center" mt={6}>
            <Button
              onClick={() =>
                setVisible((v) => ({ ...v, [typeKey]: v[typeKey] + (isMobile ? 4 : 6) }))
              }
              bg="#DC242C"
              color="white"
              fontWeight="900"
              borderRadius="16px"
              _hover={{ filter: "brightness(0.95)" }}
            >
              Load more
            </Button>
          </Flex>
        ) : null}
      </Box>
    );
  };

  return (
    <Box>
      <PageHeader />

      <Box py={{ base: 10, md: 12 }}>
        <Container maxW="1200px">
          {/* Filters */}
          <Box
            border="1px solid"
            borderColor={border}
            bg={panelBg}
            borderRadius="22px"
            p={{ base: 4, md: 5 }}
            mb={8}
          >
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} alignItems="center">
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color={useColorModeValue("gray.500", "gray.400")} />
                </InputLeftElement>
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search title, authors, venue, date…"
                  borderRadius="14px"
                />
              </InputGroup>

              <Select value={year} onChange={(e) => setYear(e.target.value)} borderRadius="14px">
                <option value="All">All years</option>
                {years.map((y) => (
                  <option key={y} value={String(y)}>
                    {y}
                  </option>
                ))}
              </Select>

              
            </SimpleGrid>

            <HStack mt={4} spacing={3} flexWrap="wrap">
              <Tag borderRadius="full" fontWeight="900">
                Showing{" "}
                {filteredByType.journals.length +
                  filteredByType.conferences.length +
                  filteredByType.patents.length}{" "}
                items
              </Tag>

              {(query || year !== "All") ? (
                <Button
                  size="sm"
                  variant="ghost"
                  fontWeight="900"
                  onClick={() => {
                    setQuery("");
                    setYear("All");
                  }}
                >
                  Clear filters
                </Button>
              ) : null}
            </HStack>
          </Box>

          {/* Sections */}
          <Stack spacing={{ base: 10, md: 12 }}>
            {hasActiveFilter && totalFilteredCount === 0 ? (
              <Box
                border="1px solid"
                borderColor={border}
                bg={panelBg}
                borderRadius="22px"
                p={{ base: 8, md: 10 }}
                textAlign="center"
              >
                <Heading
                  fontSize={{ base: "xl", md: "2xl" }}
                  color={useColorModeValue("#315369", "white")}
                  mb={2}
                >
                  No publication found
                </Heading>
                <Text color={useColorModeValue("gray.600", "gray.400")}>
                  Try a different keyword or adjust the year filter.
                </Text>
              </Box>
            ) : (
              visibleSections.map((section, index) => (
                <React.Fragment key={section.typeKey}>
                  <Section
                    label={section.label}
                    items={section.items}
                    typeKey={section.typeKey}
                    emptyText={section.emptyText}
                  />
                  {index < visibleSections.length - 1 ? <Divider borderColor={border} /> : null}
                </React.Fragment>
              ))
            )}
          </Stack>

          <Divider mt={{ base: 10, md: 12 }} borderColor={border} />
        </Container>
      </Box>
    </Box>
  );
}
